import {
  ANALYTICS_PAGE_TYPE,
  DEFAULT_LANGUAGE,
  EVENT,
  pushToDataLayer,
} from '@wr/web-shared';
import {
  AppContextProviderShared,
  getCountryCodeFromLocale,
  getSendCountryCodeFromLocale,
} from '@wr/web-ui';
import type { GetServerSideProps } from 'next';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { Layout } from '@/components/layout';
import { PageMetadataComponent } from '@/components/page-metadata';
import {
  DEFAULT_COUNTRY_PAGE,
  DEFAULT_LOCATION,
  GENERIC_CLP_SLUG,
} from '@/constants';
import { AppContextProvider } from '@/context';
import {
  getContentfulCountriesArticlesAndPrepositions,
  getPage,
  getPageData,
} from '@/services';
import { getCountryArticlesAndPrepositionsByCode } from '@/services/contentful/country/country.utils';
import { getFaqItems } from '@/services/contentful/faq/faq.service';
import { parseFaqItems } from '@/services/contentful/faq/faq.utils';
import { getContentfulPageQueryVariables } from '@/services/contentful/page/page.utils';
import { getRedirects } from '@/services/contentful/redirects';
import { getLinksWithoutRedirects } from '@/services/contentful/redirects/redirect.utils';
import {
  Countries,
  CountryDataProps,
  parseCountryDataPartial,
} from '@/services/rest';
import { CountryData } from '@/services/rest/countries.service';
import { useCountriesStore } from '@/state';
import { CountryPageProps } from '@/types';
import {
  getAllModuleItems,
  logger,
  selectPayoutMethodsLabelsById,
} from '@/utils';
import { getBreadcrumbs } from '@/utils/breadcrumbs.utils';
import { resolveComponents } from '@/utils/component-resolver';
import { resolveState } from '@/utils/state-resolver';

import { AppPropsWithLayout } from '../_app';

const Page: FC<CountryPageProps & AppPropsWithLayout> = ({
  __typename,
  modules,
  name,
  isWebView,
  preview,
  sys,
  title,
  description,
  breadcrumbs,
  noIndex,
  header,
  originUri,
  footer,
  countries,
  regions,
  messages,
  pageLinksWithRegionsAndSlugs,
  analyticsPageType,
  sendCountry,
  receiveCountry,
  countryArticlesAndPrepositions,
  payoutMethodsLabelsById,
  locale,
  slug,
  faqItems,
  faqCountries,
}) => {
  const hydrate = useCountriesStore(state => state.hydrate);

  useEffect(() => {
    if (countries) hydrate(countries);
  }, [hydrate, countries]);

  const sendCountryData = useMemo(() => {
    let country = null;

    if (sendCountry?.iso2) {
      country = countries?.find(country => country.iso2 === sendCountry?.iso2);
    }

    if (!country && countries?.length > 1) {
      country = countries[0];
    }

    if (!country) return null;

    return parseCountryDataPartial(country);
  }, [countries, sendCountry]);

  const metadataPath = useMemo(() => {
    if (!slug) return '/countries';
    if (slug.endsWith(`/${sendCountry?.iso2}`)) return `/countries/${slug}`;
    return `/countries/${slug}/${sendCountry?.iso2}`;
  }, [sendCountry, slug]);

  const resolvedComponents = resolveComponents(
    modules || [],
    {
      CountriesGrid: {
        countries,
        countryArticlesAndPrepositions,
      },
    },
    header,
  );

  const resolvedState = resolveState(modules || [], {
    FaqSection: {
      receiveCountry: receiveCountry?.iso2,
      faqItems,
      faqCountries,
    },
  });

  // Contentful data from PayoutMethod and Country content type entries
  const intlContextValue = useMemo(
    () => ({
      countryArticlesAndPrepositions,
      payoutMethodsLabelsById,
    }),
    [countryArticlesAndPrepositions, payoutMethodsLabelsById],
  );

  const [fullUrl, setFullUrl] = useState('');
  useEffect(() => {
    setFullUrl(window.location.href);
  }, []);

  useEffect(() => {
    pushToDataLayer({
      event: EVENT.PAGE_LOADED,
      page_type: analyticsPageType,
      page_name: fullUrl,
      page_language: locale,
      sender_country: sendCountry?.name,
      sender_country_iso: sendCountry?.iso2.toUpperCase(),
    });
  }, [analyticsPageType, fullUrl, locale, sendCountry]);

  return (
    <AppContextProviderShared
      isWebView={isWebView}
      originUri={originUri}
      regions={regions as string[]}
      messages={messages}
      sendCountry={sendCountryData}
      receiveCountry={parseCountryDataPartial(receiveCountry)}
      intl={intlContextValue}
      locale={locale}
    >
      <AppContextProvider placeholder="TODO: Add SW specific props here">
        <PageMetadataComponent
          title={title}
          description={description}
          originUri={originUri}
          pathname={metadataPath}
          noIndex={noIndex}
          pageLinksWithRegionsAndSlugs={pageLinksWithRegionsAndSlugs}
          regions={regions}
          analyticsPageType={analyticsPageType}
        />
        {resolvedState}
        <Layout
          __typename={__typename}
          name={name}
          preview={preview}
          sys={sys}
          footer={{ ...footer }}
          header={isWebView ? null : header}
          breadcrumbs={breadcrumbs}
          languageFromCookie={getCountryCodeFromLocale(locale)}
        >
          {resolvedComponents}
        </Layout>
      </AppContextProvider>
    </AppContextProviderShared>
  );
};

export const getServerSideProps: GetServerSideProps<
  CountryPageProps | Record<string, unknown>
> = async ({
  preview = false,
  locale: nextLocale,
  defaultLocale,
  params,
  query,
}) => {
  const pageType = ANALYTICS_PAGE_TYPE.Country;
  const region = (() => {
    if (nextLocale && nextLocale !== defaultLocale) return nextLocale;
    return DEFAULT_LANGUAGE;
  })();
  const locale = region;

  const sendCountryOptionsInOrderOfPreference: string[] = [];
  if (query?.from)
    sendCountryOptionsInOrderOfPreference.push(query?.from as string);
  if (params?.slug?.[1])
    sendCountryOptionsInOrderOfPreference.push(params.slug[1]);
  sendCountryOptionsInOrderOfPreference.push(
    getSendCountryCodeFromLocale(nextLocale),
  );
  sendCountryOptionsInOrderOfPreference.push(DEFAULT_LOCATION);

  const receive = (params?.slug?.[0] as string) || null;

  const { slug } = await getContentfulPageQueryVariables({
    params,
  });

  const res = await Countries.get();
  let countries: CountryData[] = res.data || [];

  // add valid receive countries as an array to each send country
  countries = countries.map(country => {
    return {
      ...country,
      validSendCountriesIso2: countries
        .filter(sendCountry =>
          sendCountry.validReceiveCountriesIso2?.includes(
            country.iso2.toLowerCase(),
          ),
        )
        .map((country: CountryData) => country.iso2.toLowerCase()),
    };
  });

  if (!countries || countries.length === 0) {
    logger.error(res, 'Countries were not found');
  }

  const receiveCountry =
    countries?.find(country => {
      const countryNameSlug = country.name.toLowerCase().replace(/\s+/g, '-');
      const receiveLower = receive?.toLowerCase();
      return (
        (countryNameSlug === receiveLower ||
          country.iso2.toLowerCase() === receiveLower) &&
        country.receive
      );
    }) ?? null;

  const sendCountries = countries?.filter(country => country.send) || [];

  const validSendCountry =
    sendCountryOptionsInOrderOfPreference.find(sendCountryCode => {
      const sendCountryCodes = countries
        .filter(country => country.send)
        .map(country => country.iso2?.toLowerCase());
      if (sendCountryCodes.includes(sendCountryCode?.toLowerCase()))
        return true;
    }) ?? DEFAULT_LOCATION;

  const sendCountry =
    countries?.find(country => {
      const sendLower = validSendCountry?.toLowerCase();
      const countryNameSlug = country.name.toLowerCase().replace(/\s+/g, '-');
      const sendCountryCodeLower = validSendCountry.toLowerCase();

      return (
        (country.iso2.toLowerCase() === sendLower ||
          countryNameSlug === sendLower ||
          country.iso2.toLowerCase() === sendCountryCodeLower) &&
        country.send
      );
    }) ?? null;

  if (!receiveCountry && slug) {
    logger.error({ slug }, 'Receive Country was not found');

    return {
      notFound: true,
    };
  }

  if (
    slug &&
    receiveCountry &&
    receiveCountry.validSendCountriesIso2?.length === 0
  ) {
    logger.error({ slug }, 'Receive Country has no valid send countries');
    return {
      notFound: true,
    };
  }

  let page = await getPage({
    slug: slug || DEFAULT_COUNTRY_PAGE,
    locale,
    region,
    preview,
  });

  // if we don't have a specific country page, we want to fetch the generic CLP
  if (!page) {
    page = await getPage({
      slug: GENERIC_CLP_SLUG,
      locale,
      region: DEFAULT_LANGUAGE,
      preview,
    });
  }

  if (!page) {
    logger.error({ slug }, 'Page was not found');

    return {
      notFound: true,
    };
  }

  // Normalize locale for Contentful queries (en-gb -> en, en-us -> en, etc.)
  const contentfulLocale = locale.split('-')[0];

  const [
    pageData,
    modules,
    pageRedirects,
    countryArticlesPrepositions,
    contentfulFaqs,
  ] = await Promise.all([
    getPageData({
      pageId: page.sys.id,
      locale,
      slug: page?.slug || '',
      analyticsPageType: pageType || '',
      preview,
      sendCountryCode: sendCountry?.iso2 || DEFAULT_LOCATION,
    }),
    getAllModuleItems({
      locale,
      modules: page?.modulesCollection?.items,
      preview,
    }),
    getRedirects(),
    getContentfulCountriesArticlesAndPrepositions({
      countryCodes: countries.map((country: CountryData) =>
        country.iso2.toLowerCase(),
      ),
      locale: contentfulLocale,
      preview,
    }),
    getFaqItems(contentfulLocale, preview),
  ]);

  const { faqItems, faqCountries } = parseFaqItems(contentfulFaqs);

  const breadcrumbs = getBreadcrumbs(pageData);

  // A NOTE ON COUNTRY PAGE REVIEWS: ReviewsCard type was deleted from Contentful
  // If re-enabled in future, restore getReviewsByCountryCode call

  const regionSlug = page.regions?.map(region => ({
    slug: slug ? `${region}/countries/${slug}` : `${region}/countries`,
    title: region || '',
  }));

  const pageLinksWithRegionsAndSlugs = getLinksWithoutRedirects(
    regionSlug,
    pageRedirects,
  );

  const regions =
    page.regions?.filter((region): region is string => Boolean(region)) || [];

  const countryArticlesAndPrepositions =
    countryArticlesPrepositions &&
    getCountryArticlesAndPrepositionsByCode(countryArticlesPrepositions);
  const payoutMethodsLabelsById = selectPayoutMethodsLabelsById(pageData);

  const countryPageCountries: CountryDataProps[] = [
    ...sendCountries.filter(
      country =>
        receiveCountry &&
        country.validReceiveCountriesIso2?.includes(receiveCountry.iso2),
    ),
  ];
  if (receiveCountry) countryPageCountries.push(receiveCountry);

  return {
    props: {
      sys: page.sys,
      __typename: page.__typename,
      name: page.name,
      title: page.title,
      description: page.description,
      noIndex: page.noIndex,
      footer: {
        ...page.footer,
        legalMessage: pageData?.legalMessagingCollection?.items[0],
      },
      header: {
        ...pageData?.layoutHeaderCollection?.items[0],
        headerPosition: page.headerPosition,
        headerTheme: page.headerTheme,
      },
      breadcrumbs,
      modules,
      originUri: process.env.WEB_CMS_SW_URI,
      countries: receiveCountry ? countryPageCountries ?? [] : countries || [],
      sendCountry,
      receiveCountry,
      regions,
      messages: pageData?.messagesCollection?.items[0] || {},
      pageLinksWithRegionsAndSlugs,
      analyticsPageType: pageType,
      countryArticlesAndPrepositions,
      payoutMethodsLabelsById,
      locale,
      slug,
      faqCountries: faqCountries || [],
      faqItems: faqItems || [],
    },
  };
};

export default Page;
